<template>
    <div class="container">
        <div class="row mat32">
            <div class="col-12 title">Wiadomości</div>
        </div>
        <div class="row mat24 flex-nowrap">
            <!-- Sidebar. -->
            <div class="side-bar none-mobile">
                <base-button-group>
                    <base-button
                        btn11
                        @click="$router.push({ name: 'panel-competition-mail-new', params: competition, alias: true })"
                    >
                        Wyślij e-mail
                    </base-button>
                    <base-button
                        v-if="canAccessSms"
                        btn11
                        @click="$router.push({ name: 'panel-competition-sms-new', params: competition, alias: true })"
                    >
                        Wyślij SMS
                    </base-button>
                </base-button-group>
            </div>
            <div v-if="competition" class="content-wrap">
                <validation-observer v-slot="{ handleSubmit }" ref="FormObserver" tag="div">
                    <panel-competition-form-box>
                        <p>Wiadomości SMS</p>
                        <p class="mat16">
                            SMS zostanie wysłany wyłącznie do uczestników, którzy zapisali się na
                            <b>{{ competition.name }}</b>
                            i podali numer telefonu. Pamiętaj, że celem wysyłki jest komunikat dotyczący organizacji
                            Twoich zawodów (np. zmiana godziny startu lub odwołanie imprezy). Wiadomość nie powinna być
                            treścią marketingową ani spamem.
                        </p>
                    </panel-competition-form-box>
                    <panel-competition-form-box class="mat16">
                        <div class="col-12 col-lg-8">
                            <validation-provider
                                v-slot="{ errors }"
                                name="information"
                                rules="required"
                                class="scroll-margin relative"
                            >
                                <panel-input-area
                                    ref="messageArea"
                                    v-model="form.information"
                                    label="Treść SMS"
                                    :rows="8"
                                    :error.sync="errors.information"
                                />
                                <span v-if="errors[0]" class="input-error-alt">Pole wymagane</span>
                            </validation-provider>
                            <div v-if="form.information" class="segment-info mat8">
                                <span>
                                    {{ segmentInfo.adjustedLength }} znaków ·
                                    {{ segmentInfo.encoding === 'gsm' ? 'GSM-7bit' : 'UCS-2' }} ·
                                    <template v-if="segmentInfo.segments != null">
                                        {{ segmentInfo.segments }}
                                        {{ segmentInfo.segments === 1 ? 'część SMS' : 'części SMS' }}
                                    </template>
                                    <template v-else>—</template>
                                </span>
                                <span
                                    v-if="!segmentInfo.isValid && form.information.length > 0"
                                    class="segment-info__error"
                                >
                                    SMS jest pusty lub za długi (max {{ maxSegments }} części).
                                </span>
                            </div>
                        </div>
                        <div class="col-12 col-lg-3">
                            <p class="tags-title mat24">Znaczniki personalizacji</p>
                            <p class="tags-desc">Kliknij znacznik, aby wstawić go do treści:</p>
                            <ul class="tags-list">
                                <li v-for="tag in availableTags" :key="tag.key">
                                    <button type="button" class="tag-btn" @click="insertTag(tag.key)">
                                        [{{ tag.key }}]
                                    </button>
                                    <span class="tag-label">{{ tag.label }}</span>
                                </li>
                            </ul>
                        </div>
                    </panel-competition-form-box>
                    <panel-competition-form-box class="mat16">
                        <div class="w-full mab16">
                            <p class="title">Opcje wysyłki</p>
                        </div>
                        <div class="radio-wrap">
                            <div class="radio-wrap-column">
                                <label class="mab8">Wyślij do uczestników</label>
                                <panel-input-base-radio
                                    v-for="option in sendPaymentOptions"
                                    :key="option.value"
                                    v-model="form.send_payment"
                                    name="formSendPayment"
                                    :option="option"
                                />
                            </div>
                            <div class="radio-wrap-column">
                                <label class="mab8">Wyślij do uczestników z wybranej kategorii</label>
                                <panel-input-base-radio
                                    v-for="option in classificationOptions"
                                    :key="option.value"
                                    v-model="form.send_classification"
                                    name="formSendClassification"
                                    :option="option"
                                />
                            </div>
                            <div class="radio-wrap-column">
                                <p>Liczba odbiorców</p>
                                <b
                                    v-if="!loadingCount"
                                    class="recipient-count"
                                    :class="{ 'recipient-count--zero': recipientCount === 0 }"
                                    >{{ recipientCount }}</b
                                >
                                <span v-else class="recipient-count recipient-count--loading">Ładowanie...</span>
                                <p v-if="!loadingCount && recipientCount === 0" class="no-recipients-warning">
                                    Brak odbiorców z numerem telefonu spełniających wybrane kryteria.
                                </p>
                            </div>
                        </div>
                    </panel-competition-form-box>
                    <p v-if="submitError" class="submit-error">{{ submitError }}</p>
                    <p v-if="submitSuccess" class="submit-success">{{ submitSuccess }}</p>
                    <div class="actions-wrap">
                        <base-button
                            btn1
                            short
                            :disabled="isWaiting || recipientCount === 0 || loadingCount || !segmentInfo.isValid"
                            @click="handleSubmit(openConfirmModal)"
                        >
                            <span v-if="!isWaiting">Utwórz wysyłkę SMS</span>
                            <span v-else>Zapisywanie...</span>
                        </base-button>
                    </div>
                </validation-observer>

                <!-- SMS history table -->
                <div class="mailings-section mat32">
                    <p class="mailings-section__title">Wysłane wiadomości SMS</p>
                    <div v-if="loadingSmses" class="mailings-loading">Ładowanie listy SMS...</div>
                    <div v-else-if="smsError" class="mailings-error">{{ smsError }}</div>
                    <table v-else class="mailings-table">
                        <thead>
                            <tr>
                                <th>Treść</th>
                                <th>Odbiorcy</th>
                                <th>Cena</th>
                                <th>Status</th>
                                <th>Wysłano / Błędy</th>
                                <th>Data</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="smses.length === 0">
                                <td colspan="6" class="mailings-table__empty">Brak wysłanych wiadomości SMS</td>
                            </tr>
                            <tr
                                v-for="sms in smses"
                                :key="sms.id"
                                class="mailings-table__row"
                                @click="openSmsDetail(sms.id)"
                            >
                                <td class="sms-preview">{{ truncate(sms.information, 60) }}</td>
                                <td>{{ sms.smses_count }}</td>
                                <td>{{ formatPrice(sms.price) }} zł</td>
                                <td>
                                    <span class="status-badge" :class="`status-badge--${sms.status || 'pending'}`">
                                        {{ statusLabel(sms.status) }}
                                    </span>
                                </td>
                                <td>
                                    {{ sms.smses_sent ?? '—' }} /
                                    {{ sms.smses_not_sent ?? '—' }}
                                </td>
                                <td>{{ formatUnixDate(sms.sended) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Confirm SMS billing modal -->
                <modal ref="confirmModal" body-class="h-auto" class="sms-confirm-modal">
                    <template #header>
                        <span>Potwierdzenie wysyłki SMS</span>
                    </template>
                    <template #body>
                        <div class="sms-confirm-modal__body">
                            <p>
                                Kwota <b>1 zł × {{ recipientCount }}</b> uczestników zostanie dopisana do Twojego
                                rachunku, a faktura zostanie wystawiona na koniec miesiąca.
                            </p>
                        </div>
                    </template>
                    <template #footer>
                        <button
                            type="button"
                            class="sms-confirm-modal__cancel"
                            :disabled="isWaiting"
                            @click="closeConfirmModal"
                        >
                            Anuluj
                        </button>
                        <base-button btn1 :disabled="isWaiting" @click="submit">
                            <span v-if="!isWaiting">Akceptuję i wyślij</span>
                            <span v-else>Wysyłanie...</span>
                        </base-button>
                    </template>
                </modal>

                <!-- SMS detail modal -->
                <modal ref="smsModal" size="xl">
                    <template #header>
                        <span>Szczegóły SMS</span>
                    </template>
                    <template #body>
                        <div v-if="loadingDetail" class="modal-loading">Ładowanie szczegółów...</div>
                        <div v-else-if="selectedSms" class="mailing-detail">
                            <div class="mailing-detail__row">
                                <span class="mailing-detail__label">Status:</span>
                                <span class="status-badge" :class="`status-badge--${selectedSms.status || 'pending'}`">
                                    {{ statusLabel(selectedSms.status) }}
                                </span>
                            </div>
                            <div class="mailing-detail__row">
                                <span class="mailing-detail__label">Odbiorcy:</span>
                                <span>{{ selectedSms.smses_count }}</span>
                            </div>
                            <div class="mailing-detail__row">
                                <span class="mailing-detail__label">Cena:</span>
                                <span>{{ formatPrice(selectedSms.price) }} zł</span>
                            </div>
                            <div class="mailing-detail__row">
                                <span class="mailing-detail__label">Wysłano / Błędy:</span>
                                <span
                                    >{{ selectedSms.smses_sent ?? '—' }} / {{ selectedSms.smses_not_sent ?? '—' }}</span
                                >
                            </div>
                            <div class="mailing-detail__row">
                                <span class="mailing-detail__label">Data utworzenia:</span>
                                <span>{{ formatUnixDate(selectedSms.sended) }}</span>
                            </div>
                            <div class="mailing-detail__row">
                                <span class="mailing-detail__label">Opłacono:</span>
                                <span>{{ formatDate(selectedSms.paid_at) }}</span>
                            </div>
                            <div class="mailing-detail__row">
                                <span class="mailing-detail__label">Kategoria:</span>
                                <span>{{ selectedSms.players_classification || '—' }}</span>
                            </div>
                            <div class="mailing-detail__row">
                                <span class="mailing-detail__label">Płatność:</span>
                                <span>{{ paymentFilterLabel(selectedSms.players_pay) }}</span>
                            </div>
                            <div v-if="selectedSms.information" class="mailing-detail__row mailing-detail__row--col">
                                <span class="mailing-detail__label">Treść:</span>
                                <pre class="mailing-detail__content">{{ selectedSms.information }}</pre>
                            </div>
                            <div v-if="selectedSms.smses_error" class="mailing-detail__row mailing-detail__row--col">
                                <span class="mailing-detail__label">Błędy:</span>
                                <p class="mailing-detail__error-list">{{ selectedSms.smses_error }}</p>
                            </div>
                        </div>
                    </template>
                    <template #footer>
                        <div />
                        <base-button btn2 short @click="$refs.smsModal.closeModal()">Zamknij</base-button>
                    </template>
                </modal>
            </div>
        </div>
    </div>
</template>

<script>
    import axios from 'axios';
    import { mapGetters } from 'vuex';
    import { ValidationObserver, ValidationProvider, extend } from 'vee-validate';
    import { required } from 'vee-validate/dist/rules';

    import PanelCompetitionFormBox from '@/components/PanelCompetitionFormBox';
    import PanelInputArea from '@/components/PanelInputArea';
    import PanelInputBaseRadio from '@/components/PanelInputBaseRadio';
    import Modal from '@/components/Modal';
    import { getSmsSegmentInfo, MAX_SEGMENTS } from '@/utils/smsSegments';

    extend('required', {
        ...required,
        message: 'required_field',
    });

    export default {
        name: 'PanelCompetitionMessagesSms',
        components: {
            ValidationObserver,
            ValidationProvider,
            PanelCompetitionFormBox,
            PanelInputArea,
            PanelInputBaseRadio,
            Modal,
        },
        props: {
            competition: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                isWaiting: false,
                form: {
                    information: '',
                    send_classification: 'all',
                    send_payment: 'all',
                },
                recipientCount: 0,
                loadingCount: false,
                smses: [],
                loadingSmses: false,
                smsError: null,
                selectedSms: null,
                loadingDetail: false,
                submitError: null,
                submitSuccess: null,
                maxSegments: MAX_SEGMENTS,
                availableTags: [
                    { key: 'firstname', label: 'imię' },
                    { key: 'lastname', label: 'nazwisko' },
                    { key: 'playernumber', label: 'numer startowy' },
                    { key: 'date', label: 'data startu' },
                    { key: 'pay_link', label: 'link do płatności' },
                    { key: 'site_link', label: 'link do rejestracji' },
                ],
                sendPaymentOptions: [
                    { value: 'all', title: 'Wszystkim' },
                    { value: '1', title: 'Tylko opłaconym' },
                    { value: '0', title: 'Tylko nieopłaconym' },
                ],
            };
        },
        computed: {
            ...mapGetters(['user', 'classificationsBy']),
            canAccessSms() {
                return this.user?.roles?.includes('ROLE_ADMIN') || this.user?.roles?.includes('ROLE_OPERATOR');
            },
            classifications() {
                return this.classificationsBy(this.competition);
            },
            classificationOptions() {
                const base = [{ value: 'all', title: 'Wszystkim' }];
                if (!this.classifications || this.classifications.length === 0) {
                    return base;
                }
                const items = this.classifications.map((c) => ({
                    value: String(c.id),
                    title: c.short || c.namePl,
                }));
                return [...base, ...items];
            },
            segmentInfo() {
                return getSmsSegmentInfo(this.form.information);
            },
        },
        watch: {
            'form.send_classification': function () {
                this.fetchRecipientCount();
            },
            'form.send_payment': function () {
                this.fetchRecipientCount();
            },
        },
        mounted() {
            if (!this.canAccessSms) {
                this.$router.replace({
                    name: 'panel-competition-mail-new',
                    params: this.competition,
                    alias: true,
                });
                return;
            }
            this.fetchRecipientCount();
            this.fetchSmses();
        },
        methods: {
            openConfirmModal() {
                if (!this.segmentInfo.isValid || this.recipientCount === 0) return;
                this.submitError = null;
                this.submitSuccess = null;
                this.$refs.confirmModal.openModal();
            },

            closeConfirmModal() {
                if (this.isWaiting) return;
                this.$refs.confirmModal.closeModal();
            },

            async fetchRecipientCount() {
                if (!this.competition) return;
                this.loadingCount = true;
                try {
                    const result = await axios({
                        method: 'get',
                        url: `/panel/competitions/${this.competition.id}/smses/count`,
                        params: {
                            send_classification: this.form.send_classification,
                            send_payment: this.form.send_payment,
                        },
                    });
                    this.recipientCount =
                        typeof result === 'object' ? result.recipients_count ?? result.count ?? 0 : result;
                } catch (error) {
                    console.error('fetchRecipientCount error:', error);
                    this.recipientCount = 0;
                } finally {
                    this.loadingCount = false;
                }
            },

            async fetchSmses() {
                if (!this.competition) return;
                this.loadingSmses = true;
                this.smsError = null;
                try {
                    const result = await axios({
                        method: 'get',
                        url: `/panel/competitions/${this.competition.id}/smses`,
                    });
                    this.smses = Array.isArray(result) ? result : [];
                } catch (error) {
                    console.error('fetchSmses error:', error);
                    this.smsError = 'Nie udało się pobrać listy SMS.';
                } finally {
                    this.loadingSmses = false;
                }
            },

            async openSmsDetail(id) {
                this.selectedSms = null;
                this.loadingDetail = true;
                this.$refs.smsModal.openModal();
                try {
                    const result = await axios({
                        method: 'get',
                        url: `/panel/smses/${id}`,
                    });
                    this.selectedSms = result;
                } catch (error) {
                    console.error('openSmsDetail error:', error);
                } finally {
                    this.loadingDetail = false;
                }
            },

            /**
             * Creates SMS sending request. Cost is billed to the operator account (monthly invoice).
             */
            async submit() {
                this.submitError = null;
                this.submitSuccess = null;
                if (!this.segmentInfo.isValid || this.recipientCount === 0) return;

                try {
                    this.isWaiting = true;
                    await axios({
                        method: 'post',
                        url: `/panel/competitions/${this.competition.id}/smses`,
                        data: {
                            information: this.form.information,
                            send_classification: this.form.send_classification,
                            send_payment: this.form.send_payment,
                            type: 'newsletter',
                        },
                    });
                    this.$refs.confirmModal.closeModal();
                    this.form.information = '';
                    this.form.send_classification = 'all';
                    this.form.send_payment = 'all';
                    this.$refs.FormObserver.reset();
                    this.submitSuccess =
                        'Wysyłkę SMS utworzono. Kwota zostanie dopisana do Twojego rachunku — faktura na koniec miesiąca.';
                    await this.fetchSmses();
                    this.$emit('update');
                } catch (error) {
                    console.error('submit error:', error);
                    this.submitError =
                        (typeof error === 'object' && (error.error || error.message)) ||
                        'Nie udało się utworzyć wysyłki SMS.';
                } finally {
                    this.isWaiting = false;
                }
            },

            insertTag(key) {
                const tag = `[${key}]`;
                const textarea = this.$refs.messageArea?.$el?.querySelector('textarea');
                if (textarea) {
                    const start = textarea.selectionStart ?? textarea.value.length;
                    const end = textarea.selectionEnd ?? textarea.value.length;
                    const current = this.form.information;
                    this.form.information = current.slice(0, start) + tag + current.slice(end);
                    this.$nextTick(() => {
                        textarea.focus();
                        const newPos = start + tag.length;
                        textarea.setSelectionRange(newPos, newPos);
                    });
                } else {
                    this.form.information += tag;
                }
            },

            statusLabel(status) {
                const map = {
                    pending: 'Oczekuje',
                    paid: 'Opłacony',
                    sent: 'Wysłany',
                    failed: 'Błąd',
                };
                return map[status] ?? status ?? '—';
            },

            paymentFilterLabel(value) {
                const map = {
                    all: 'Wszyscy',
                    1: 'Opłaceni',
                    0: 'Nieopłaceni',
                };
                return map[value] ?? value ?? '—';
            },

            formatDate(dateStr) {
                if (!dateStr) return '—';
                return String(dateStr).replace('T', ' ').slice(0, 16);
            },

            formatUnixDate(ts) {
                if (!ts) return '—';
                const d = new Date(Number(ts) * 1000);
                if (Number.isNaN(d.getTime())) return '—';
                const pad = (n) => String(n).padStart(2, '0');
                return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
                    d.getMinutes()
                )}`;
            },

            formatPrice(value) {
                if (value == null || value === '') return '—';
                const num = Number(value);
                if (Number.isNaN(num)) return value;
                return num.toFixed(2).replace('.', ',');
            },

            truncate(text, max) {
                if (!text) return '—';
                return text.length > max ? `${text.slice(0, max)}…` : text;
            },
        },
    };
</script>

<style scoped>
    .title {
        font-family: 'Bariol Bold';
        font-size: 18px;
    }

    .side-bar {
        width: 165px;
    }

    .w-full {
        width: 100%;
    }

    .input-error-alt {
        display: block;
        color: var(--color-pinky-red, red);
        font-size: 12px;
        margin-top: 4px;
    }

    .segment-info {
        font-size: 12px;
        color: var(--color-bluish-grey);
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .segment-info__error {
        color: var(--color-pinky-red);
    }

    .radio-wrap {
        width: 100%;
        display: flex;
        flex-wrap: nowrap;
        gap: 16px;
    }

    .radio-wrap label {
        display: block;
    }

    .radio-wrap-column {
        flex: 1 1 0;
        min-width: 0;
        width: 33.333%;
    }

    .recipient-count {
        font-family: 'Bariol Bold';
        font-size: 18px;
    }

    .recipient-count--loading {
        font-size: 14px;
        color: var(--color-bluish-grey);
        font-weight: normal;
    }

    .recipient-count--zero {
        color: var(--color-pinky-red);
    }

    .no-recipients-warning {
        margin-top: 6px;
        font-size: 12px;
        color: var(--color-pinky-red);
        background: var(--color-pinky-red-10);
        border-radius: 4px;
        padding: 6px 10px;
        display: block;
    }

    .submit-error {
        color: var(--color-pinky-red);
        font-size: 13px;
        margin-top: 8px;
    }

    .submit-success {
        color: var(--color-dark-pastel-green);
        font-size: 13px;
        margin-top: 8px;
    }

    .actions-wrap {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        padding: 24px 0;
    }

    .tags-title {
        font-family: 'Bariol Bold';
        font-size: 14px;
    }

    .tags-desc {
        font-size: 12px;
        /* color: var(--color-bluish-grey); */
        margin-top: 4px;
        margin-bottom: 8px;
    }

    .tags-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    .tags-list li {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .tag-btn {
        background: var(--color-pale-grey);
        border: 1px solid var(--color-silver);
        border-radius: 4px;
        padding: 2px 8px;
        font-size: 12px;
        color: var(--color-slate);
        cursor: pointer;
        font-family: monospace;
        transition: background 0.15s, border-color 0.15s;
        flex-shrink: 0;
    }

    .tag-btn:hover {
        background: var(--color-silver);
        border-color: var(--color-cool-grey);
    }

    .tag-label {
        font-size: 12px;
        /* color: var(--color-bluish-grey); */
    }

    .mailings-section {
        border-top: 1px solid var(--color-silver);
        padding-top: 24px;
    }

    .mailings-section__title {
        font-family: 'Bariol Bold';
        font-size: 18px;
        margin-bottom: 16px;
    }

    .mailings-loading {
        padding: 16px;
        font-size: 14px;
        color: var(--color-bluish-grey);
    }

    .mailings-error {
        padding: 16px;
        font-size: 14px;
        color: var(--color-pinky-red);
    }

    .mailings-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
    }

    .mailings-table th {
        background: var(--color-pale-grey);
        color: var(--color-slate-two);
        padding: 10px 12px;
        text-align: left;
        font-family: 'Bariol Bold';
        font-weight: normal;
        border-bottom: 2px solid var(--color-silver);
        white-space: nowrap;
    }

    .mailings-table td {
        padding: 10px 12px;
        border-bottom: 1px solid var(--color-light-grey);
        vertical-align: middle;
        color: var(--color-greyish-brown);
    }

    .mailings-table__row {
        cursor: pointer;
        transition: background 0.15s;
        background: var(--color-light-grey);
    }

    .mailings-table__row:hover td {
        background: var(--color-gray-hover);
    }

    .mailings-table__empty {
        text-align: center;
        color: var(--color-cool-grey);
        padding: 24px;
    }

    .sms-preview {
        max-width: 280px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .status-badge {
        display: inline-block;
        padding: 2px 8px;
        border-radius: 10px;
        font-size: 11px;
        font-weight: bold;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    .status-badge--pending {
        background: rgba(176, 190, 197, 0.25);
        color: var(--color-slate-two);
    }

    .status-badge--paid {
        background: var(--color-light-sage-40);
        color: var(--color-charcoal-grey);
    }

    .status-badge--sent {
        background: rgba(80, 174, 85, 0.15);
        color: var(--color-dark-pastel-green);
    }

    .status-badge--failed {
        background: var(--color-pinky-red-10);
        color: var(--color-pinky-red);
    }

    .modal-loading {
        padding: 24px;
        color: var(--color-bluish-grey);
    }

    .mailing-detail {
        padding: 24px;
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .mailing-detail__row {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .mailing-detail__row--col {
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;
    }

    .mailing-detail__label {
        font-family: 'Bariol Bold';
        color: var(--color-slate-two);
        min-width: 120px;
        flex-shrink: 0;
    }

    .mailing-detail__content {
        background: var(--color-pale-grey);
        border: 1px solid var(--color-silver);
        border-radius: 4px;
        padding: 12px;
        font-size: 13px;
        white-space: pre-wrap;
        word-break: break-word;
        max-height: 200px;
        overflow-y: auto;
        margin: 0;
    }

    .mailing-detail__error-list {
        font-size: 12px;
        color: var(--color-pinky-red);
        background: var(--color-light-grey);
        border-radius: 4px;
        padding: 8px 12px;
        word-break: break-all;
        margin: 0;
    }

    @media (min-width: 992px) {
        .content-wrap {
            margin-left: 30px;
        }
    }

    @media (max-width: 991px) {
        .radio-wrap {
            flex-wrap: wrap;
        }

        .radio-wrap-column {
            flex: 1 1 100%;
            width: 100%;
        }
    }

    .sms-confirm-modal >>> .modal__dialog {
        width: 614px;
        max-width: calc(100vw - 32px);
    }

    .sms-confirm-modal >>> .modal__body {
        height: auto;
    }

    .sms-confirm-modal__body {
        padding: 24px 16px 8px;
        color: var(--color-slate-two);
        font-size: 14px;
        line-height: 20px;
    }

    .sms-confirm-modal__cancel {
        min-width: 160px;
        height: 40px;
        padding: 8px 16px;
        border: 0;
        background: transparent;
        color: var(--color-bluish-grey);
        font-size: 14px;
        line-height: 20px;
        text-transform: uppercase;
        cursor: pointer;
    }

    .sms-confirm-modal__cancel:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
</style>
